'use client'

import { content } from '@/config/content'
import { useSiteSettings } from '@/lib/useSiteSettings'

export default function Overseas({ items = [] }) {
  const { settings } = useSiteSettings('pricing')
  const fileDownload = Number(settings.price_file_download || 2500)

  // Phase 3.3 — intro / payment / note are CMS-driven with legacy fallback.
  // Special ids split off from the articles list so admin can edit them.
  const introRow = items.find((i) => i.id === 'overseas-intro')
  const paymentRow = items.find((i) => i.id === 'overseas-payment')
  const noteRow = items.find((i) => i.id === 'overseas-note')
  const articles = items.filter((i) => !['overseas-intro', 'overseas-payment', 'overseas-note'].includes(i.id))

  const introText = introRow?.body || content.overseas.description
  const paymentText = paymentRow?.body || content.overseas.payment
  const noteText = noteRow?.body || content.overseas.note

  return (
    <section style={{ padding: '0 var(--spacing-pagePadding) 48px' }}>
      <div className="max-w-lg mx-auto">
        {/* Intro */}
        {introText.split('\n').map((line, i) => (
          <p
            key={i}
            className="text-sm leading-7 mb-2 stagger-fade-in"
            style={{ color: 'var(--color-textSecondary)', '--stagger-delay': `${i * 0.08}s` }}
          >
            {line}
          </p>
        ))}

        {/* 2026-10-17 店主指定放這頁:28 秒直式短片(檔案放 public/videos,跟著 repo 走,搬站不會漏)。
            preload="none" 不點不載,手機不白耗流量;同網域檔案,CSP default-src 'self' 已涵蓋。 */}
        <figure className="mt-8 mx-auto stagger-fade-in" style={{ maxWidth: '340px', '--stagger-delay': '0.16s' }}>
          <div className="overflow-hidden" style={{ borderRadius: '16px', background: '#000', boxShadow: '0 10px 28px rgba(0,0,0,.12)' }}>
            <video
              src="/videos/meowmi-short-v2.mp4"
              poster="/videos/meowmi-short-v2-poster.jpg"
              controls
              playsInline
              preload="none"
              width={720}
              height={1280}
              className="block w-full h-auto"
              style={{ aspectRatio: '9 / 16', maxHeight: '78vh', objectFit: 'contain' }}
            />
          </div>
          <figcaption className="text-xs text-center mt-3" style={{ color: 'var(--color-textMuted)' }}>
            28 秒看懂：認領圖・線上購圖・紋身貼紙
          </figcaption>
        </figure>

        {/* Decorative divider */}
        <div className="flex items-center justify-center gap-2 my-10">
          <span style={{ width: '24px', height: '1px', background: 'var(--color-text)', opacity: 0.15 }} />
          <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--color-accent)', opacity: 0.4 }} />
          <span style={{ width: '24px', height: '1px', background: 'var(--color-text)', opacity: 0.15 }} />
        </div>

        {/* DB content — sub-articles (excludes intro/payment/note special rows) */}
        {articles.length > 0 && (
          <div className="space-y-12">
            {articles.map((item, idx) => (
              <article
                key={item.id}
                className="stagger-fade-in"
                style={{ '--stagger-delay': `${(idx + 2) * 0.08}s` }}
              >
                <h3
                  className="accent-label text-sm font-bold mb-4 tracking-wide"
                  style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text)' }}
                >
                  {item.title}
                </h3>
                {item.body && (
                  <div className="space-y-2">
                    {item.body.split('\n').filter(Boolean).map((line, i) => (
                      <p key={i} className="text-sm leading-7" style={{ color: 'var(--color-textSecondary)' }}>
                        {line}
                      </p>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {/* Decorative divider */}
        <div className="flex items-center justify-center gap-2 my-10">
          <span style={{ width: '24px', height: '1px', background: 'var(--color-text)', opacity: 0.15 }} />
          <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--color-accent)', opacity: 0.4 }} />
          <span style={{ width: '24px', height: '1px', background: 'var(--color-text)', opacity: 0.15 }} />
        </div>

        {/* Unified price (same as domestic, managed via PricingTab) */}
        <div className="text-center stagger-fade-in" style={{ '--stagger-delay': '0.3s' }}>
          <span className="accent-label text-xs tracking-widest uppercase block mb-2" style={{ color: 'var(--color-textMuted)' }}>
            Price
          </span>
          <p className="text-2xl font-medium" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}>
            NT${fileDownload.toLocaleString()}
          </p>
          <p className="text-xs mt-2" style={{ color: 'var(--color-accent)', fontWeight: 500 }}>
            此為線上圖檔授權費用，不含實體刺青操作
          </p>
          <p className="text-xs mt-2" style={{ color: 'var(--color-textMuted)' }}>
            {paymentText}
          </p>
        </div>

        <p className="text-xs mt-10 stagger-fade-in" style={{ color: 'var(--color-textMuted)', '--stagger-delay': '0.5s' }}>
          {noteText}
        </p>
      </div>
    </section>
  )
}
